/**
 * Represents the ProductCard in order confirmation
 *
 * @module views/modules/SummaryProductCard
 * @memberof -Common
 */
import './SummaryProductCard.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import Text from '@ulta/core/components/Text/Text';

import BagProductCardDetails from '@ulta/components/BagProductCardDetails/BagProductCardDetails';
import BagProductCardPrice from '@ulta/components/BagProductCardPrice/BagProductCardPrice';
import BagProductCardProductImage from '@ulta/components/BagProductCardProductImage/BagProductCardProductImage';


/**
 * Represents a SummaryProductCard component
 *
 * @method
 * @param {SummaryProductCardProps} props - React properties passed from composition
 * @returns SummaryProductCard
 */


export const SummaryProductCard = ( props ) => {
  const {
    ariaLabel,
    appliedMessages,
    brandName,
    componentKey,
    commonLabelAction,
    deals,
    finalPrice,
    image,
    listPrice,
    listPriceAccessibilityLabel = defaultProps.listPriceAccessibilityLabel,
    productName,
    productNameTag,
    quantityLabel,
    salePriceAccessibilityLabel = defaultProps.salePriceAccessibilityLabel,
    subscriptionIndicator,
    subsEditAction,
    unitPriceBreakup,
    variantImage,
    variantTypeDesc,
    variantTypeLabel,
    subsIndicator
  } = props;

  const elementRef = useRef( null );

  if( !brandName && !productName ){
    return null;
  }

  return (
    <div
      aria-label={ ariaLabel }
      ref={ elementRef }
      className='SummaryProductCard'
      role='group'
    >
      <div className='SummaryProductCard__ContentWrapper'>
        { image?.imageUrl && (
          <BagProductCardProductImage
            imageUrl={ image.imageUrl }
            action={ image.action }
            metaData={ image.metaData }
          />
        ) }
        <div className='SummaryProductCard__DetailsWrapper'>
          <div className='SummaryProductCard__Left'>
            <BagProductCardDetails
              brandName={ brandName }
              componentKey={ componentKey }
              image={ image }
              productName={ productName }
              productNameTag={ productNameTag }
              variantImage={ variantImage }
              variantTypeDesc={ variantTypeDesc }
              variantTypeLabel={ variantTypeLabel }
              subscriptionCart={ subsIndicator }
              commonLabelAction={ commonLabelAction }
            />
          </div>
          <div className='SummaryProductCard__Right'>
            { quantityLabel && (
              <div className='Quantity__Label'>
                <Text
                  htmlTag='p'
                  textStyle='body-2'
                  color='neutral-600'
                >
                  { quantityLabel }
                </Text>
              </div>
            ) }
            <BagProductCardPrice
              appliedMessages={ appliedMessages }
              componentKey={ componentKey }
              deals={ deals }
              listPrice={ listPrice }
              listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
              salePrice={ finalPrice }
              salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
              unitPriceBreakup={ unitPriceBreakup }
              subscriptionIndicator={ subscriptionIndicator }
              subsEditAction={ subsEditAction }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef BagProductCardProps
 * @type {object}
 * @property {object} image - image props
 * @property {string} ariaLabel - aria label for card
 * @property {string} brandName - brand name
 * @property {object} commonLabelAction - Action on click of labels like productName, brandName
 * @property {string} listPrice - products listing price
 * @property {string} listPriceAccessibilityLabel - accessiblity label for products listing price
 * @property {string} productName - product name
 * @property {string} productNameTag - html tag for product name
 * @property {object} variantImage - variant image props
 * @property {string} variantLabel - product description label
 * @property {string} variantTypeDesc - product description
 * @property {string} variantImage - sets the image url for product
 * @property {string} finalPrice - product final price
 * @property {string} quantityLabel - provides text for quantity label
 * @property {string} salePriceAccessibilityLabel - accessibility label for product final price
 * @property {object} subscriptionIndicator - Label and icon for subscription
 * @property {object} subsEditAction - Edit action for subscription links to subscription page
 * @property {string} unitPriceBreakup - unit price breakup label
 * @property {array} deals -   sets of deals
 * @property {string} componentKey - Sets the componentKey
 */
export const propTypes = {
  appliedMessages: PropTypes.arrayOf(
    PropTypes.shape( {
      icon: PropTypes.string,
      iconColor: PropTypes.string,
      message: PropTypes.string
    } )
  ),
  image: PropTypes.shape( {
    name: PropTypes.string,
    action: PropTypes.shape( {
      url: PropTypes.string
    } ),
    imageUrl: PropTypes.string,
    metaData: PropTypes.shape( {
      width: PropTypes.number,
      height: PropTypes.number,
      aspectRatio: PropTypes.shape( {
        w: PropTypes.string,
        h: PropTypes.number
      } )
    } )
  } ),
  brandName: PropTypes.string,
  commonLabelAction: PropTypes.shape( {
    url: PropTypes.string
  } ),
  listPrice: PropTypes.string,
  listPriceAccessibilityLabel: PropTypes.string,
  productName: PropTypes.string,
  productNameTag: PropTypes.string,
  variantImage: PropTypes.shape( {
    name: PropTypes.string,
    imageUrl: PropTypes.string
  } ),
  variantTypeLabel: PropTypes.string,
  variantTypeDesc: PropTypes.string,
  finalPrice: PropTypes.string,
  deals: PropTypes.array,
  quantityLabel: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
  salePriceAccessibilityLabel: PropTypes.string,
  subscriptionIndicator: PropTypes.object,
  subsEditAction: PropTypes.object,
  unitPriceBreakup: PropTypes.string,
  componentKey: PropTypes.string,
  subsIndicator: PropTypes.shape( {
    subsIcon: PropTypes.string,
    subsLabel: PropTypes.string
  } )
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} listPriceAccessibilityLabel - accessiblity label for products listing price
 * @property {string} salePriceAccessibilityLabel - accessiblity label for products final price
 */
export const defaultProps = {
  listPriceAccessibilityLabel: 'Original price is %1',
  salePriceAccessibilityLabel: 'Sale price is %1'
};

SummaryProductCard.defaultProps = defaultProps;
SummaryProductCard.propTypes = propTypes;

export default SummaryProductCard;